import { useState, useEffect } from 'react';
import { clinicSettingsService } from '../services/clinicSettingsService';
import type { ClinicSettings, PublicClinicSettings, UseClinicSettingsResult } from '../types/clinic';

function extractName(data: ClinicSettings | PublicClinicSettings): string {
  if ('clinic_name' in data) {
    return data.clinic_name || '';
  }
  return data.name || '';
}

export function useClinicSettings(doctorId?: number): UseClinicSettingsResult {
  const [clinicName, setClinicName] = useState<string>(
    import.meta.env.VITE_CLINIC_NAME || 'eCliniq'
  );
  const [clinicAddress, setClinicAddress] = useState<string>(
    import.meta.env.VITE_CLINIC_ADDRESS || ''
  );
  const [clinicPhone, setClinicPhone] = useState<string>(
    import.meta.env.VITE_CLINIC_PHONE || ''
  );
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [letterheadUrl, setLetterheadUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function fetchSettings() {
      try {
        setLoading(true);
        const settings = doctorId
          ? await clinicSettingsService.getSettingsByDoctor(doctorId)
          : await clinicSettingsService.getSettings();

        if (cancelled) return;

        if (settings) {
          setClinicName(extractName(settings) || import.meta.env.VITE_CLINIC_NAME || 'eCliniq');
          setClinicAddress(settings.address ?? import.meta.env.VITE_CLINIC_ADDRESS ?? '');
          setClinicPhone(settings.phone ?? import.meta.env.VITE_CLINIC_PHONE ?? '');
          setLogoUrl(settings.logo_url ?? null);
          setLetterheadUrl('letterhead_url' in settings ? settings.letterhead_url : null);
        }
        setError(null);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Error fetching clinic settings');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    fetchSettings();

    return () => {
      cancelled = true;
    };
  }, [doctorId]);

  return { clinicName, clinicAddress, clinicPhone, logoUrl, letterheadUrl, loading, error };
}
